/**
 * `/median-income` 페이지에서 사용하는 모든 정적 데이터.
 *
 * ─── 구조 ─────────────────────────────────────────────────────────────────
 * - `MEDIAN_INCOME_BY_YEAR`: 한 연도의 모든 데이터(수치·4대급여·정책·FAQ·콘텐츠·콘셉트 카드)를
 *   하나의 번들로 묶은 후, 키를 연도 문자열로 두어 다년도를 동시에 보유한다.
 *   → 추후 '2027' 추가 시 `MEDIAN_INCOME_BY_YEAR['2027'] = { ... }` 한 줄로 확장.
 * - 연도와 무관하게 항상 동일한 값(`HOUSEHOLD_OPTIONS`, `BENEFIT_ORDER`, `GLOSSARY`)만
 *   번들 바깥에 둔다.
 * - UI 의 연도 탭은 `MedianIncomeYear` 타입을 따라 `MEDIAN_INCOME_BY_YEAR` 의 키를 순회한다.
 *
 * ─── 출처 ─────────────────────────────────────────────────────────────────
 * 보건복지부 2026 기준 중위소득 고시(2025-08-01) + 통계청 가계금융복지조사.
 * 정책/콘텐츠/급여의 외부 링크(path/link/moreLink)는 1차 출시에 비활성.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 0) 공통 타입
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 기초생활보장 4대 급여 식별자.
 * - edu: 교육급여 / hou: 주거급여 / med: 의료급여 / liv: 생계급여
 */
export type BenefitKey = 'edu' | 'hou' | 'med' | 'liv';

/** ⑦ 4대 급여 자격 카드 한 장에 필요한 정보. */
export type BenefitDetail = {
  /** 카드 좌측 아이콘(이모지). */
  icon: string;
  /** 급여 이름 ("교육급여" 등). */
  name: string;
  /** 자격 기준 % — 사용자 비율 ≤ 이 값이면 카드 노출. */
  thresholdPercent: number;
  /** 카드 본문 1줄 설명. */
  desc: string;
  /** 카드 본문 bullet 항목들. */
  list: readonly string[];
  /**
   * 정책 상세 페이지 경로 (`/policy/{uuid}`).
   * 매핑 없으면 카드 클릭 비활성.
   */
  url?: string;
};

/** ⑤ 정책 카드 한 장. */
export type TierPolicy = {
  /** 정책명. */
  name: string;
  /** React key 용 내부 식별자. */
  slug: string;
  /** 한 줄 요약. */
  summary: string;
  /** 신청 대상. */
  target: string;
  /** 지원 금액. */
  amount: string;
  /**
   * 정책 상세 페이지 경로 (`/policy/{encoded-name}--{uuid}`).
   * 매핑이 없는 정책은 undefined — 카드에서 "자세히 보기" 버튼이 노출되지 않는다.
   */
  url?: string;
};

/** ⑨ FAQ 한 문항. */
export type FaqItem = {
  /** 질문 plain text. */
  q: string;
  /** 답변. `<strong>` 등 인라인 HTML 허용 — 화면에는 dangerouslySetInnerHTML 로 노출. */
  a: string;
};

/** ⑧ 추천 콘텐츠 카드 한 장. */
export type RecommendedContent = {
  /** 카드 본문 제목. 2줄 line-clamp 으로 노출. */
  title: string;
  /** 콘텐츠 상세 페이지 경로 (`/community/policyInfo/{slug}--{uuid}`). */
  url: string;
  /** 카드 썸네일 이미지 URL. */
  thumbnail: string;
};

/** ⑬ "중위소득 한눈에 알아두기" 섹션의 카드 한 장. */
export type ConceptCard = {
  /** 카드 제목("산정 방법" 등). */
  title: string;
  /** 카드 본문. `<strong>` 등 인라인 HTML 허용. */
  body: string;
  /** 본문 아래 작은 보조 문구(선택). */
  footnote?: string;
};

/** 용어집 항목 (연도 무관). */
export type GlossaryItem = {
  /** 용어 이름. */
  term: string;
  /** 정의. `<strong>` 등 인라인 HTML 허용. */
  def: string;
};

/**
 * 한 연도의 전체 데이터 번들.
 * 다년도 확장 시 같은 구조로 추가만 하면 된다.
 */
export type MedianIncomeYearBundle = {
  /** 출처 표기 문구 ("보건복지부 2026 고시" 등). */
  source: string;
  /**
   * 가구원수별 중위소득 100% 월 금액(원).
   * index 0 = 1인 ~ index 6 = 7인 이상 가구의 기준액(baseline).
   */
  householdBaselines: readonly number[];
  /** ④ 표1 에 노출할 % 구간 목록 (큰 값 → 작은 값). */
  tiers: readonly number[];
  /** ⑦ 4대 급여 카드 상세 설명. */
  benefitDetail: Record<BenefitKey, BenefitDetail>;
  /** ⑤ 중위소득 % 구간 → 노출할 정책 카드. 키는 `tiers` 의 값과 1:1 대응(문자열). */
  policiesByTier: Record<string, readonly TierPolicy[]>;
  /** ⑨ 자주 묻는 질문. 순서가 표시 순서. */
  faqList: readonly FaqItem[];
  /** ⑧ 추천 콘텐츠 카드. */
  recommendedContents: readonly RecommendedContent[];
  /** ⑬ 핵심 개념 카드. */
  conceptCards: readonly ConceptCard[];
};

// ─────────────────────────────────────────────────────────────────────────────
// 1) 연도 무관 공용 상수 (탭 전환과 무관)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ② 계산기의 가구원 수 select 옵션.
 * - value: hundred 배열의 인덱스 + 1 (즉 1~7).
 * - 7은 "7인 이상" 의미로 동일하게 7인 기준 금액을 사용한다.
 */
export const HOUSEHOLD_OPTIONS = [
  { value: 1, label: '1인' },
  { value: 2, label: '2인' },
  { value: 3, label: '3인' },
  { value: 4, label: '4인' },
  { value: 5, label: '5인' },
  { value: 6, label: '6인' },
  { value: 7, label: '7인 이상' },
] as const;

/**
 * 4대 급여 카드 노출 순서(자격 기준 % 큰 → 작은 순).
 * 사용자 비율 ≤ b.thresholdPercent 인 급여만 필터링되어 이 순서로 노출.
 */
export const BENEFIT_ORDER: readonly BenefitKey[] = [
  'edu',
  'hou',
  'med',
  'liv',
];

/**
 * ⑬ "함께 알아둘 개념" 용어집 — 연도 무관 정의.
 * 부양의무자 기준은 정책 변화에 따라 수정 가능성 있으나, 현재 정의는 안정적이라 공용으로 둔다.
 */
export const GLOSSARY: readonly GlossaryItem[] = [
  {
    term: '소득인정액',
    def: '= 소득평가액 + 재산의 소득환산액 (실제 소득보다 낮을 수 있음)',
  },
  {
    term: '경상소득',
    def: '= 근로소득 + 사업소득 + 재산소득 + 이전소득',
  },
  {
    term: '부양의무자 기준',
    def: '1촌 직계혈족(부모·자녀) 및 그 배우자의 소득·재산을 함께 보는 기준. <strong>2026년 현재 의료급여에만 적용</strong>, 생계·주거·교육급여는 폐지됨',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 2) 년도별 번들 (MEDIAN_INCOME_BY_YEAR)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 연도별 전체 데이터 맵.
 * - `MEDIAN_INCOME_BY_YEAR['2026']` 형태로 접근.
 * - 신규 연도 추가는 키만 늘리면 끝나며, `MedianIncomeYear` 타입과 `MEDIAN_INCOME_YEARS` 가
 *   자동으로 새 키를 인식한다.
 */
const BUNDLE_2026 = {
  source: '보건복지부 2026 고시',
  // 1인, 2인, 3인, 4인, 5인, 6인, 7인+ (단위: 원)
  householdBaselines: [
    2564238, 4199292, 5359036, 6494738, 7556719, 8555952, 9555185,
  ],
  // ④ 표1: 250%~50% 구간
  tiers: [250, 150, 120, 100, 80, 75, 70, 65, 60, 50],
  benefitDetail: {
    edu: {
      icon: '📚',
      name: '교육급여',
      thresholdPercent: 50,
      desc: '기초생활보장 교육급여는 중위소득 50% 이하 가구의 초·중·고 재학생에게 학비·교과서·교육활동지원비를 지원합니다.',
      list: [
        '지원 내용: 입학금·수업료(공립 무상, 사립은 가산금), 교과서, 교육활동지원비',
        '신청 대상: 가구 소득인정액이 기준 중위소득 50% 이하인 가구의 초·중·고 재학생',
        '지원 금액(연): 초 487,000원 · 중 679,000원 · 고 768,000원 (교육활동지원비 기준)',
      ],
      url: '/policy/762b656d-8b2c-4520-9913-3ce80bcc66ea',
    },
    hou: {
      icon: '🏠',
      name: '주거급여',
      thresholdPercent: 48,
      desc: '주거급여는 중위소득 48% 이하 가구의 임차료(임차가구) 또는 주택 수선유지비(자가가구)를 지원합니다.',
      list: [
        '지원 내용: 임차가구는 월 임차료, 자가가구는 주택 수선유지비',
        '신청 대상: 가구 소득인정액이 기준 중위소득 48% 이하',
        '지원 금액: 임차료 지역·가구원수별 기준임대료까지 (서울 4인 기준 약 56만원)',
      ],
      url: '/policy/02da5059-d6c0-4ae7-9310-4e4c451fad9b',
    },
    med: {
      icon: '🏥',
      name: '의료급여',
      thresholdPercent: 40,
      desc: '의료급여는 중위소득 40% 이하 가구의 의료비를 본인부담 최소화로 지원합니다(부양의무자 기준 적용).',
      list: [
        '지원 내용: 진찰·검사·약제·수술·입원 등 본인부담금 경감',
        '신청 대상: 가구 소득인정액이 기준 중위소득 40% 이하 + 부양의무자 기준 충족',
        '본인부담: 1종(입원 무료, 외래 1,000~2,000원) / 2종(외래 15%·입원 10%)',
      ],
      url: '/policy/c8f3555f-d4d8-47db-9dd7-b7b0ff59a317',
    },
    liv: {
      icon: '💵',
      name: '생계급여',
      thresholdPercent: 32,
      desc: '생계급여는 중위소득 32% 이하 가구에 월 단위 현금을 지급하여 최저 생활을 보장합니다.',
      list: [
        '지원 내용: 월 단위 현금 지급(가구 소득인정액과 선정기준액의 차액)',
        '신청 대상: 가구 소득인정액이 기준 중위소득 32% 이하',
        '최대 지급액(4인): 월 2,078,316원에서 가구 소득인정액 차감',
      ],
      url: '/policy/c26b5f7a-8b4a-410c-948d-d1e4b6156e9d',
    },
  },
  policiesByTier: {
    '250': [
      {
        name: '아이돌봄서비스',
        slug: 'child-care-service',
        summary: '취업 부모를 위한 정부 지원 시간제·종일제 돌봄 서비스',
        target: '만 12세 이하 자녀를 둔 가구',
        amount: '시간당 본인부담 1,500~12,400원',
        url: '/policy/%EC%95%84%EC%9D%B4%EB%8F%8C%EB%B4%84%EC%84%9C%EB%B9%84%EC%8A%A4--fa2e8dce-1302-438f-a391-c59446dcd520',
      },
    ],
    '150': [
      {
        name: '산모·신생아 건강관리',
        slug: 'newborn-care',
        summary: '출산 가정에 산모·신생아 건강관리사 지원',
        target: '출산 가정 산모',
        amount: '5~25일, 본인부담 차등',
        url: '/policy/%EC%82%B0%EB%AA%A8%EC%8B%A0%EC%83%9D%EC%95%84-%EA%B1%B4%EA%B0%95%EA%B4%80%EB%A6%AC--ca49d473-e7ff-4555-87b4-7a1fd472d999',
      },
      {
        name: '양육비 선지급 제도',
        slug: 'child-support-advance',
        summary: '양육비를 받지 못한 한부모 가정에 정부가 선지급',
        target: '한부모 가정',
        amount: '월 최대 20만원',
        url: '/policy/%EC%96%91%EC%9C%A1%EB%B9%84-%EC%84%A0%EC%A7%80%EA%B8%89-%EC%A0%9C%EB%8F%84--c42ec2d1-e1b6-41f2-b2f4-676285a5c866',
      },
    ],
    '120': [
      {
        name: '국민취업지원제도(청년)',
        slug: 'korea-job-program-youth',
        summary: '청년 구직자 취업활동비 + 직업훈련 지원',
        target: '만 15~34세 미취업 청년',
        amount: '월 50~90만원, 6개월',
        url: '/policy/%EA%B5%AD%EB%AF%BC%EC%B7%A8%EC%97%85%EC%A7%80%EC%9B%90%EC%A0%9C%EB%8F%84--7d2f4ca5-6850-422e-a994-f44f2a836075',
      },
      {
        name: '치매검사비',
        slug: 'dementia-screening',
        summary: '치매 조기 검진 비용 지원',
        target: '만 60세 이상',
        amount: '검사비 최대 15만원',
        url: '/policy/%EC%B9%98%EB%A7%A4%EA%B2%80%EC%82%AC%EB%B9%84--33fbd976-eb77-49ba-8957-1020ad887319',
      },
    ],
    '100': [
      {
        name: '저소득층 기저귀·조제분유',
        slug: 'diaper-formula-support',
        summary: '영아 기저귀·조제분유 구입비 지원',
        target: '만 2세 미만 영아 양육 가구',
        amount: '월 9만원(기저귀) + 11만원(분유)',
        url: '/policy/%EC%A0%80%EC%86%8C%EB%93%9D%EC%B8%B5-%EA%B8%B0%EC%A0%80%EA%B7%80%EC%A1%B0%EC%A0%9C%EB%B6%84%EC%9C%A0--cb17398c-0212-4167-994b-f8368acd8ce1',
      },
      {
        name: '위기청소년 특별지원',
        slug: 'youth-emergency',
        summary: '위기 청소년 생활·학업·의료비 긴급 지원',
        target: '만 9~24세 위기 청소년',
        amount: '최대 100만원/월',
        url: '/policy/%EC%9C%84%EA%B8%B0%EC%B2%AD%EC%86%8C%EB%85%84-%ED%8A%B9%EB%B3%84-%EC%A7%80%EC%9B%90--012a5d1f-8bd6-4272-a40f-b3e58d40e222',
      },
      {
        name: '국민취업지원제도(2유형)',
        slug: 'korea-job-program-2',
        summary: '직업훈련·일경험 + 취업활동비',
        target: '구직 의사 있는 미취업자',
        amount: '월 50만원 × 6개월',
        url: '/policy/%EA%B5%AD%EB%AF%BC%EC%B7%A8%EC%97%85%EC%A7%80%EC%9B%90%EC%A0%9C%EB%8F%84--7d2f4ca5-6850-422e-a994-f44f2a836075',
      },
      {
        name: '청년전세임대',
        slug: 'youth-rental-house',
        summary: 'LH가 청년 대신 전세계약 → 저렴한 임대료로 재임대',
        target: '만 19~39세 무주택 청년',
        amount: '보증금 최대 1.2억원 지원',
        url: '/policy/%EC%B2%AD%EB%85%84%EC%A0%84%EC%84%B8%EC%9E%84%EB%8C%80--3bd10c04-5a49-421f-a5da-4bcf46e5b1ec',
      },
    ],
    '80': [
      {
        name: '임산부·영유아 영양플러스',
        slug: 'nutrition-plus',
        summary: '임산부·영유아 보충식품 패키지 무상 지원',
        target: '임산부·만 6세 미만 영유아',
        amount: '월 평균 6만원 상당 식품',
        url: '/policy/%EC%9E%84%EC%82%B0%EB%B6%80-%EB%B0%8F-%EC%98%81%EC%9C%A0%EC%95%84-%EC%98%81%EC%96%91%ED%94%8C%EB%9F%AC%EC%8A%A4--781aeddb-a010-4e57-b81d-cb3143f0a131',
      },
      {
        name: '초·중·고 교육비',
        slug: 'school-education-fee',
        summary: '학비·급식비·방과후 자유수강권 지원',
        target: '초·중·고 재학생',
        amount: '연 최대 230만원 상당',
        url: '/policy/%EC%B4%88%EC%A4%91%EA%B3%A0-%EA%B5%90%EC%9C%A1%EB%B9%84--5ab64fd5-63f1-11f0-bc8a-06341b517e27',
      },
    ],
    '75': [
      {
        name: '긴급복지 주거지원',
        slug: 'emergency-housing',
        summary: '갑작스러운 위기로 거처를 잃은 가구에 임시 거주 지원',
        target: '위기 사유 발생 가구',
        amount: '월 최대 66만원 × 3개월',
        url: '/policy/%EA%B8%B4%EA%B8%89%EB%B3%B5%EC%A7%80-%EC%A3%BC%EA%B1%B0%EC%A7%80%EC%9B%90--3ee517b9-0ab0-4233-80d2-c2b4116f5f6c',
      },
      {
        name: '긴급복지 생계지원',
        slug: 'emergency-livelihood',
        summary: '위기 가구에 즉시 생계비 지급',
        target: '위기 사유 발생 가구',
        amount: '월 최대 187만원(4인) × 3개월',
        url: '/policy/%EA%B8%B4%EA%B8%89%EB%B3%B5%EC%A7%80-%EC%83%9D%EA%B3%84%EC%A7%80%EC%9B%90--c4f7f50b-d710-4a97-b337-6d1f2dea7998',
      },
    ],
    '70': [
      {
        name: '가사·간병 방문지원',
        slug: 'home-care-visit',
        summary: '거동이 불편한 저소득 가구에 가사·간병 서비스 제공',
        target: '만 65세 미만 거동 불편자',
        amount: '월 24~40시간 서비스',
        url: '/policy/%EA%B0%80%EC%82%AC%EA%B0%84%EB%B3%91-%EB%B0%A9%EB%AC%B8-%EC%A7%80%EC%9B%90--b82ba6d3-de55-4eba-990f-2d4adb296729',
      },
      {
        name: '영구임대주택공급',
        slug: 'permanent-rental-house',
        summary: '저소득 가구 대상 시세 30% 수준 임대주택 공급',
        target: '기초수급·차상위·국가유공자 등',
        amount: '보증금·임대료 일반시세 30%',
        url: '/policy/%EC%98%81%EA%B5%AC%EC%9E%84%EB%8C%80%EC%A3%BC%ED%83%9D%EA%B3%B5%EA%B8%89--8ad863bb-d7fe-43b1-9e3a-8de90042c745',
      },
    ],
    '65': [
      {
        name: '한부모가족 아동양육비',
        slug: 'single-parent-child-support',
        summary: '한부모 가족 18세 미만 자녀 양육비 지원',
        target: '한부모·조손가족',
        amount: '월 23만원(자녀 1인당)',
        url: '/policy/%ED%95%9C%EB%B6%80%EB%AA%A8%EA%B0%80%EC%A1%B1-%EC%95%84%EB%8F%99%EC%96%91%EC%9C%A1%EB%B9%84-%EC%A7%80%EC%9B%90--9f652b65-5ddb-40b9-b73c-2d1693079ee8',
      },
      {
        name: '여성청소년 생리용품',
        slug: 'menstrual-product-support',
        summary: '여성청소년 생리용품 구입 지원',
        target: '만 9~24세 여성청소년',
        amount: '월 1.4만원 바우처',
        url: '/policy/%EC%97%AC%EC%84%B1%EC%B2%AD%EC%86%8C%EB%85%84-%EC%83%9D%EB%A6%AC%EC%9A%A9%ED%92%88--0122bb0d-ed9f-4939-bea3-374425f36703',
      },
      {
        name: '노인 무릎인공관절 수술 지원',
        slug: 'knee-surgery-support',
        summary: '노인성 퇴행성관절염 인공관절 수술비',
        target: '만 60세 이상',
        amount: '최대 120만원',
        url: '/policy/%EB%85%B8%EC%9D%B8-%EB%AC%B4%EB%A6%8E%EC%9D%B8%EA%B3%B5%EA%B4%80%EC%A0%88-%EC%88%98%EC%88%A0-%EC%A7%80%EC%9B%90--004212e3-7d18-4e1c-92bd-4d0af1f8af0a',
      },
    ],
    '60': [
      {
        name: '국민취업지원제도(1유형)',
        slug: 'korea-job-program-1',
        summary: '저소득 구직자 구직촉진수당 + 취업활동 지원',
        target: '저소득 미취업자',
        amount: '월 50만원 × 6개월 + 부양가족수당',
        url: '/policy/%EA%B5%AD%EB%AF%BC%EC%B7%A8%EC%97%85%EC%A7%80%EC%9B%90%EC%A0%9C%EB%8F%84--7d2f4ca5-6850-422e-a994-f44f2a836075',
      },
    ],
    '50': [
      {
        name: '저소득층 에너지 효율 개선',
        slug: 'energy-efficiency-support',
        summary: '단열·창호·보일러 등 주택 에너지 효율 개선 시공',
        target: '기초·차상위 가구',
        amount: '가구당 최대 250만원',
        url: '/policy/%EC%A0%80%EC%86%8C%EB%93%9D%EC%B8%B5-%EC%97%90%EB%84%88%EC%A7%80%ED%9A%A8%EC%9C%A8%EA%B0%9C%EC%84%A0%EC%82%AC%EC%97%85%EB%82%9C%EB%B0%A9-%EC%A7%80%EC%9B%90-%EB%83%89%EB%B0%A9-%EC%A7%80%EC%9B%90--201488e9-a8fb-41ee-9a6f-4c11b664dbf4',
      },
      {
        name: '2026 문화누리카드',
        slug: 'culture-card',
        summary: '문화·여행·체육 이용 통합 카드',
        target: '기초·차상위 가구',
        amount: '연 13만원',
        url: '/policy/2026-%EB%AC%B8%ED%99%94%EB%88%84%EB%A6%AC%EC%B9%B4%EB%93%9C%ED%86%B5%ED%95%A9%EB%AC%B8%ED%99%94%EC%9D%B4%EC%9A%A9%EA%B6%8C--b5a30a06-2181-4105-b092-ec6c24abb16f',
      },
      {
        name: '자활근로',
        slug: 'self-sufficiency-work',
        summary: '근로 능력 있는 저소득층에 일자리 + 자활장려금',
        target: '기초·차상위 근로 가능자',
        amount: '월 80~160만원 + 자활장려금',
        url: '/policy/%EC%9E%90%ED%99%9C%EA%B7%BC%EB%A1%9C%EA%B8%B0%EC%B4%88-%EC%B0%A8%EC%83%81%EC%9C%84--f2e511a1-04de-4d6f-abce-89395a3c7d75',
      },
    ],
  },
  faqList: [
    {
      q: '2026년 기준 중위소득은 얼마인가요?',
      a: '<strong>4인 가구 기준 월 6,494,738원</strong>입니다. 1인 가구는 2,564,238원, 2인 4,199,292원, 3인 5,359,036원, 5인 7,556,719원, 6인 8,555,952원입니다.',
    },
    {
      q: '기준 중위소득 100%란 무슨 뜻인가요?',
      a: '전체 가구를 소득 순으로 줄세웠을 때 정확히 <strong>50번째 가구(중앙값)</strong>의 소득이 100%입니다. 평균소득이 아니라 가운데 가구의 소득이라는 점이 핵심입니다.',
    },
    {
      q: '내 소득이 중위소득의 몇 %인지 어떻게 계산하나요?',
      a: '공식은 <strong>(내 월 소득 ÷ 가구원수별 100% 기준 금액) × 100</strong>입니다. 예를 들어 4인 가구가 월 700만원이면 700만 ÷ 6,494,738 × 100 = 약 107.8%입니다. 단, 이 공식은 소득만 보는 제도 기준입니다. 기초생활보장 등은 재산까지 환산한 <strong>소득인정액</strong>으로 판정하므로 결과가 달라질 수 있습니다.',
    },
    {
      q: '생계급여를 받으려면 중위소득 몇 %까지 가능한가요?',
      a: '가구 소득인정액이 기준 중위소득 <strong>32% 이하</strong>이면 생계급여 대상입니다. 4인 가구는 월 2,078,316원 이하.',
    },
    {
      q: '의료급여·주거급여·교육급여 기준은 각각 얼마인가요?',
      a: '의료급여는 <strong>40%</strong>, 주거급여는 <strong>48%</strong>, 교육급여는 <strong>50%</strong> 이하입니다. 4인 가구 기준 각각 약 259만원·311만원·324만원.',
    },
    {
      q: '소득인정액과 실제 소득은 어떻게 다른가요?',
      a: '소득인정액은 <strong>소득평가액 + 재산의 소득환산액</strong>입니다. 실제 받은 월급뿐 아니라 보유 재산을 일정 비율로 소득으로 환산한 값을 합한 금액으로, 실제 소득보다 낮을 수도 높을 수도 있습니다.',
    },
    {
      q: '1인 가구의 중위소득 50% 금액은 얼마인가요?',
      a: '<strong>월 1,282,119원</strong>입니다. 교육급여 선정기준선이며 차상위계층 판정에도 쓰이는 값이지만, 교육급여는 월 소득 기준인 데 비해 차상위는 재산을 환산한 <strong>소득인정액</strong> 기준이라 적용 방식이 다릅니다.',
    },
    {
      q: '중위소득 기준은 매년 어떻게 결정되나요?',
      a: '보건복지부가 <strong>중앙생활보장위원회 심의·의결</strong>을 거쳐 매년 <strong>7~8월경 고시</strong>합니다. 통계청 가계금융복지조사 결과를 기반으로 가구소득 평균 증가율 등을 반영합니다.',
    },
    {
      q: '차상위계층은 어떻게 정해지나요?',
      a: '소득인정액이 기준 중위소득 <strong>50% 이하</strong>이면서 기초생활수급자가 아닌 계층입니다. 차상위 본인부담경감, 차상위 자활 등 별도 복지사업의 대상이 됩니다.',
    },
    {
      q: 'OECD 기준 취약계층·중산층은 어떻게 나누나요?',
      a: 'OECD 중산층 정의(Under Pressure, 2019)에 따르면 <strong>중위소득 75% 미만은 취약계층, 75~200%는 중산층, 200% 초과는 상류층</strong>입니다. (국내 복지제도의 50%·150% 기준선과는 별개의 분류입니다.)',
    },
    {
      q: '부양의무자 기준은 어떤 급여에 적용되나요?',
      a: '2026년 현재 <strong>의료급여에만 적용</strong>됩니다. 생계·주거·교육급여는 부양의무자 기준이 폐지되어 가구 본인의 소득인정액만으로 자격을 판단합니다.',
    },
    {
      q: '경상소득에는 어떤 소득이 포함되나요?',
      a: '경상소득은 <strong>근로소득 + 사업소득 + 재산소득 + 이전소득</strong>의 합계입니다. 일시적 소득(퇴직금·복권 등 비경상소득)은 제외됩니다.',
    },
    {
      q: '내 중위소득 %는 모든 정책에 똑같이 적용되나요?',
      a: '<strong>아니요. 같은 사람이라도 제도마다 다른 값이 나올 수 있습니다.</strong> ① 가구를 묶는 기준(누구를 한 가구로 보는지), ② 선정 기준 비율(생계급여 32% vs 청년월세 등 150%), ③ 소득 산정 방식(단순 소득 vs 재산을 환산한 소득인정액)이 제도마다 다르기 때문입니다. 계산기 결과는 대략적인 위치를 가늠하는 참고값으로 보고, 실제 자격은 신청하려는 제도의 기준으로 확인하세요.',
    },
    {
      q: '건강보험료로 중위소득을 확인할 수 있나요?',
      a: '네, 많은 제도가 건강보험료 납부액으로 간이 판정합니다. ① 판정 단위는 개인이 아니라 <strong>가구</strong>이고, ② <strong>가구원 전원의 건강보험료 본인부담금을 합산</strong>하며, ③ 직장가입자는 회사 부담분을 빼고 <strong>본인부담분만</strong> 봅니다. ④ 합산한 보험료와 가구원 수를 각 제도의 건강보험료 소득판정 기준표와 대조하면 됩니다. 기준표 금액은 제도·연도마다 다르므로 해당 사업 공고에서 확인하세요.',
    },
    {
      q: "건강보험 '세대'와 복지 '가구'는 같은 개념인가요?",
      a: '<strong>다릅니다.</strong> 건강보험 세대가 분리돼 있어도 복지 제도가 같은 가구로 보면 소득·재산이 합산되고, 반대로 같은 집에 살아도 복지 기준상 가구 분리가 인정되면 제외될 수 있습니다. 가족 중 직장가입자가 있거나 세대가 나뉘어 있더라도, 실제 합산 여부는 신청하려는 제도의 가구 기준으로 판단해야 합니다.',
    },
    {
      q: '제도마다 가구를 묶는 기준이 다른가요?',
      a: '네, 같은 중위소득을 쓰더라도 가구를 정의하는 방식이 다릅니다. <strong>기초생활보장</strong>은 주민등록표에 함께 올라 생계·주거를 같이하는 사람과 그 배우자, 30세 미만 미혼 자녀를 한 가구로 봅니다. 반면 <strong>국가장학금</strong>은 미혼이면 학생과 부모를, 기혼이면 학생과 배우자를 가구로 봅니다. 그래서 같은 사람도 어떤 제도냐에 따라 가구 구성과 합산 소득이 달라집니다.',
    },
  ],
  recommendedContents: [
    {
      title: '2026년 국민취업지원제도 1유형·2유형 지원금액·신청방법 총정리',
      url: '/community/policyInfo/2026%EB%85%84-%EA%B5%AD%EB%AF%BC%EC%B7%A8%EC%97%85%EC%A7%80%EC%9B%90%EC%A0%9C%EB%8F%84-1%EC%9C%A0%ED%98%952%EC%9C%A0%ED%98%95-%EC%A7%80%EC%9B%90%EA%B8%88%EC%95%A1-%EC%8B%A0%EC%B2%AD%EB%B0%A9%EB%B2%95-%EC%B4%9D%EC%A0%95%EB%A6%AC--8924e9ab-21fd-4ef2-80e1-d08bd29a7951',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/DS29Fschedgh6Rnt.webp',
    },
    {
      title: '2026 중위소득 50/100/150 한눈에 알려드림',
      url: '/community/policyInfo/2026-%EC%A4%91%EC%9C%84%EC%86%8C%EB%93%9D50-100-150-%ED%95%9C%EB%88%88%EC%97%90-%EC%95%8C%EB%A0%A4%EB%93%9C%EB%A6%BC--4aefad01-152d-4a66-a54b-27b63eaa791b',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/0cKB2ZBmwhuiRYNg.webp',
    },
    {
      title: '2026 정부지원금 생애주기별 강화된 지원 살펴보기',
      url: '/community/policyInfo/2026-%EC%A0%95%EB%B6%80%EC%A7%80%EC%9B%90%EA%B8%88-%EC%83%9D%EC%95%A0%EC%A3%BC%EA%B8%B0%EB%B3%84-%EA%B0%95%ED%99%94%EB%90%9C-%EC%A7%80%EC%9B%90-%EC%82%B4%ED%8E%B4%EB%B3%B4%EA%B8%B0--e2a5580a-a0be-40ae-9887-bc12c3d74629',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/JVkqnVNBRyaIGyrX.webp',
    },
    {
      title: '청년 미래적금 조건·신청, 기존 청년도약계좌 비교 정리 FAQ',
      url: '/community/policyInfo/%EC%B2%AD%EB%85%84-%EB%AF%B8%EB%9E%98%EC%A0%81%EA%B8%88-%EC%A1%B0%EA%B1%B4-%EC%8B%A0%EC%B2%AD-%EA%B8%B0%EC%A1%B4-%EC%B2%AD%EB%85%84%EB%8F%84%EC%95%BD%EA%B3%84%EC%A2%8C-%EB%B9%84%EA%B5%90-%EC%A0%95%EB%A6%AC-FAQ--0b386d49-4cb6-478f-ade7-47ee8ad0dbfc',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/BC9tFH0QSYYQoy02.webp',
    },
    {
      title: '한부모가정 지원금 인상, 종류부터 소득 조건까지',
      url: '/community/policyInfo/%ED%95%9C%EB%B6%80%EB%AA%A8%EA%B0%80%EC%A0%95-%EC%A7%80%EC%9B%90%EA%B8%88-%EC%9D%B8%EC%83%81-%EC%A2%85%EB%A5%98%EB%B6%80%ED%84%B0-%EC%86%8C%EB%93%9D-%EC%A1%B0%EA%B1%B4%EA%B9%8C%EC%A7%80--9d51501d-b7be-4050-aef4-8e80c776e63d',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/PxGTC1ZTpc6Q7Qgd.webp',
    },
    {
      title: '2026년 달라지는 청년정책 10가지 총정리',
      url: '/community/policyInfo/2026%EB%85%84-%EB%8B%AC%EB%9D%BC%EC%A7%80%EB%8A%94-%EC%B2%AD%EB%85%84%EC%A0%95%EC%B1%85-10%EA%B0%80%EC%A7%80-%EC%B4%9D%EC%A0%95%EB%A6%AC--0247db62-a7c9-489e-9139-4a284cfdecce',
      thumbnail:
        'https://cdn.welfarehello.com/production/contents/best/5HcVzCd6nAG849fR.webp',
    },
  ],
  conceptCards: [
    {
      title: '산정 방법',
      body: '기준 중위소득은 <strong>통계청 가구 경상소득(근로·사업·재산·이전 소득)의 중간값</strong>에 가구소득 평균 증가율과 가구규모별 차이를 반영해 산정합니다.',
      footnote: '결정 시점: 보건복지부가 매년 7~8월경 고시',
    },
    {
      title: '활용 범위',
      body: '현재 <strong>14개 부처 80여 개 복지사업</strong>의 수급자 선정기준으로 활용됩니다.',
      footnote:
        '대표 예시: 생계급여, 청년월세, 국민취업지원제도, 한부모가족 양육비',
    },
    {
      title: 'OECD 소득 계층 분류',
      body: 'OECD는 중위소득 대비 소득 비율로 계층을 분류합니다.',
      footnote: '출처: OECD, Under Pressure: The Squeezed Middle Class (2019)',
    },
    {
      title: '차상위계층',
      body: '<strong>소득인정액이 기준 중위소득 50% 이하</strong>이면서 기초생활수급자가 아닌 계층을 말합니다.',
      footnote:
        '차상위계층은 별도 복지사업(차상위 본인부담경감, 차상위 자활 등)의 대상이 됩니다.',
    },
  ],
} as const satisfies MedianIncomeYearBundle;

/**
 * 연도별 전체 데이터 맵.
 * - `MEDIAN_INCOME_BY_YEAR['2026']` 형태로 접근.
 * - 신규 연도 추가 시 동일 형태로 키 추가, `MedianIncomeYear` 와 `MEDIAN_INCOME_YEARS` 자동 확장.
 */
export const MEDIAN_INCOME_BY_YEAR = {
  '2026': BUNDLE_2026,
} as const satisfies Record<string, MedianIncomeYearBundle>;

// ─────────────────────────────────────────────────────────────────────────────
// 3) 년도 메타 (탭 UI 가 참조)
// ─────────────────────────────────────────────────────────────────────────────

/** 보유 중인 연도 키 union — 신규 연도 추가 시 자동 확장. */
export type MedianIncomeYear = keyof typeof MEDIAN_INCOME_BY_YEAR;

/** 탭에 노출할 연도 목록 — 과거 → 최신 오름차순(좌→우). */
export const MEDIAN_INCOME_YEARS: readonly MedianIncomeYear[] = (
  Object.keys(MEDIAN_INCOME_BY_YEAR) as MedianIncomeYear[]
)
  .slice()
  .sort((a, b) => Number(a) - Number(b));

/**
 * 페이지 진입 시 기본으로 보여줄 연도.
 *
 * 보건복지부 고시는 매년 7~8월에 다음 해 분이 미리 공표되므로, 데이터에 미래 연도가 있어도
 * 캘린더상 그 해가 되기 전까지는 직전 연도가 시행 중이다. 따라서:
 *   - 캘린더 연도 ≤ 인 가장 큰 연도를 default 로 선택.
 *   - 모두 미래(현재가 데이터의 최소 연도보다도 과거)면 보유 중 가장 오래된 연도로 폴백.
 *
 * SSR 모듈 캐싱으로 연도 경계 오류가 나지 않도록 호출 시점에 결정되는 함수로 노출한다.
 */
export const resolveDefaultMedianIncomeYear = (
  now: Date = new Date(),
): MedianIncomeYear => {
  const currentCalendarYear = now.getFullYear();
  //* MEDIAN_INCOME_YEARS 는 오름차순. 역순 탐색으로 ≤ 현재 연도 중 가장 큰 값을 선택
  //* (findLast 는 ES2023 — 구형 브라우저 미지원, sentry WELLO-8B4).
  const inEffect = [...MEDIAN_INCOME_YEARS]
    .reverse()
    .find((year) => Number(year) <= currentCalendarYear);

  return inEffect ?? MEDIAN_INCOME_YEARS[0] ?? ('2026' as MedianIncomeYear);
};
